import { useEffect, useState } from 'react';

interface TickerCard {
  id: string;
  kicker: string;
  text: string;
}

// Personal meeting principles — rotated as gentle reminders below the checklist.
export const MEETING_CARDS: TickerCard[] = [
  {
    id: 'count-five',
    kicker: 'Listen fully',
    text: 'When someone finishes, count to five before you speak.',
  },
  {
    id: 'always-recorded',
    kicker: 'Stay deliberate',
    text: 'Assume you are always being recorded — even when the meeting says it is not.',
  },
  {
    id: 'boss-on-call',
    kicker: 'Stay sharp',
    text: 'Assume your boss is on the call, even when they are not, so nothing slips out that should not.',
  },
];

const ROTATE_MS = 7_000;

interface Props {
  cards?: TickerCard[];
  intervalMs?: number;
}

export default function MeetingTicker({ cards = MEETING_CARDS, intervalMs = ROTATE_MS }: Props) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (cards.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % cards.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [cards.length, intervalMs]);

  if (cards.length === 0) return null;
  const active = cards[index];

  return (
    <section className="ticker" aria-label="Meeting reminders" aria-live="polite">
      {/* key forces a remount per card so the enter animation replays */}
      <article key={active.id} className="ticker__card">
        <span className="ticker__kicker">{active.kicker}</span>
        <p className="ticker__text">{active.text}</p>
      </article>
      <div className="ticker__dots" role="tablist" aria-label="Reminder">
        {cards.map((card, i) => (
          <button
            key={card.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Reminder ${i + 1} of ${cards.length}`}
            className={`ticker__dot${i === index ? ' ticker__dot--active' : ''}`}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>
    </section>
  );
}
